import React from 'react';
import './paperwork.css';
import { COMPANY } from '../../constants/company';

const Header = () => (
  <div className="flex items-start justify-between">
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="20" fill="#1F0068" />
        <path d="M12 22 L18 28 L28 14" fill="none" stroke="#FF5C39" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="leading-tight">
        <div className="font-bold text-[15px] text-[#1F0068]">{COMPANY.shortName}</div>
        <div className="text-[9px] tracking-[0.22em] text-[#FF5C39] uppercase font-semibold">{COMPANY.tagline}</div>
      </div>
    </div>
    <div className="text-right text-[10px] text-[#1F0068]/70 leading-relaxed">
      <div>{COMPANY.phone}</div>
      <div>{COMPANY.email}</div>
      <div>{COMPANY.registeredOffice.full}</div>
    </div>
  </div>
);

const PolicyCertificate = ({ policy }) => {
  const fullName = `${policy.title} ${policy.firstName} ${policy.lastName}`;
  const renewal = policy.policyEnd;
  return (
    <div className="a4-doc">
      <div className="a4-brand-strip" />
      <Header />

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Your Certificate</div>
        <h1 className="mt-1 text-3xl font-extrabold">Certificate of Cover</h1>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-[#FFF6EB] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Customer</div>
          <div className="mt-1 font-bold text-[15px] text-[#1F0068]">{fullName}</div>
          <div className="text-[11px] text-[#1F0068]/80">{policy.address1}</div>
          {policy.address2 && <div className="text-[11px] text-[#1F0068]/80">{policy.address2}</div>}
          <div className="text-[11px] text-[#1F0068]/80">{policy.city}, {policy.postcode}</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#FFEDD6] rounded-xl p-3"><div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Date of Issue</div><div className="font-bold text-[13px] text-[#1F0068]">{new Date(policy.createdAt || policy.policyStart).toLocaleDateString('en-GB')}</div></div>
          <div className="bg-[#FCE0EC] rounded-xl p-3"><div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Plan Number</div><div className="font-bold text-[13px] text-[#1F0068]">{policy.planNumber}</div></div>
          <div className="bg-[#E8DDFF] rounded-xl p-3"><div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Next Renewal</div><div className="font-bold text-[13px] text-[#1F0068]">{new Date(renewal).toLocaleDateString('en-GB')}</div></div>
          <div className="bg-[#FFE0D6] rounded-xl p-3"><div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Amount</div><div className="font-bold text-[13px] text-[#1F0068]">£ {Number(policy.amount).toFixed(2)}</div></div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Products Covered</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {policy.appliances.map((a) => <span key={a} className="px-2.5 py-1 bg-[#1F0068] text-white font-semibold text-[10px] rounded-full">{a}</span>)}
        </div>
      </div>

      <div className="mt-6 bg-[#FFF6EB] rounded-xl p-4 border border-[#FF5C39]/30">
        <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">Important — Confirmation of the Above Details</div>
        <p className="mt-2 text-[11px] text-[#1F0068]/85 leading-relaxed">
          Please check the details above. If any are incorrect, please contact us as soon as possible. If they are correct you do not need to do anything. Please ensure your bank transfer is completed by the payment due date to maintain uninterrupted cover. Failure to make payment by the due date may result in your plan being suspended or cancelled. A £45 to £70 administration fee may apply to cancellation requests in accordance with your Terms & Conditions.
        </p>
      </div>

      <div className="mt-6 bg-[#1F0068] text-white rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">We Now Accept Only Direct Bank Transfer Payments</div>
        <p className="mt-2 text-[11px] text-white/85 leading-relaxed">
          To ensure the highest standards of customer safety, we no longer accept card payments or cheques. All payments must be made via Direct Bank Transfer. This method helps us protect your personal and financial information.
        </p>
        <ul className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
          <li className="bg-white/10 rounded-lg px-2.5 py-1.5"><span className="font-bold text-[#FF5C39]">✓</span> Enhanced security</li>
          <li className="bg-white/10 rounded-lg px-2.5 py-1.5"><span className="font-bold text-[#FF5C39]">✓</span> Safe, reliable</li>
          <li className="bg-white/10 rounded-lg px-2.5 py-1.5"><span className="font-bold text-[#FF5C39]">✓</span> Fraud-protected</li>
          <li className="bg-white/10 rounded-lg px-2.5 py-1.5"><span className="font-bold text-[#FF5C39]">✓</span> Data protection</li>
          <li className="bg-white/10 rounded-lg px-2.5 py-1.5"><span className="font-bold text-[#FF5C39]">✓</span> Greater transparency</li>
          <li className="bg-white/10 rounded-lg px-2.5 py-1.5"><span className="font-bold text-[#FF5C39]">✓</span> Reduces unauthorised access</li>
        </ul>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
        <div className="bg-white border border-[#1F0068]/10 rounded-xl p-3">
          <div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Useful Contact Details</div>
          <div className="mt-1"><b>Helpline Number:</b> {COMPANY.phone}</div>
          <div><b>Email:</b> {COMPANY.email}</div>
        </div>
        <div className="bg-white border border-[#1F0068]/10 rounded-xl p-3">
          <div className="text-[9px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Payment Due</div>
          <div className="mt-1 font-bold text-[#FF5C39] text-lg">{new Date(policy.paymentDue).toLocaleDateString('en-GB')}</div>
        </div>
      </div>

      <div className="a4-footer">
        <div>Certificate {policy.planNumber} · {COMPANY.name} · Company No. {COMPANY.companyNumber}</div>
        <div>Registered office: {COMPANY.registeredOffice.short}</div>
      </div>
    </div>
  );
};

export default PolicyCertificate;
